/* Settings: one value per key, shared by every station, mirrored on each device.
 *
 * These are the things on the Settings page — what the board is called, what it
 * looks like, which booking system it talks to, whether it tracks requests. They
 * belong to the salon, not to the screen you happen to be standing at, so they
 * live in `tt_settings` and every station sees the same answer.
 *
 * The device mirror in localStorage is not a cache in the "nice to have" sense.
 * It is what the board boots from, always:
 *
 *   at load    the mirror is read synchronously and the app renders against it.
 *              Nothing waits on the network, so the theme is applied before the
 *              first paint rather than flashing the default and correcting
 *              itself — and a station whose internet is down comes up with the
 *              settings it had yesterday rather than with a fresh install's.
 *   on connect `hydrate` replaces those values with the server's.
 *   on change  a write updates the mirror immediately and goes into sync's
 *              durable queue, which replays it whenever the wifi returns.
 *   elsewhere  realtime on tt_settings brings another station's change here.
 *
 * The one rule that needs care: a value with a write still sitting in the queue
 * must survive a hydrate. The server has not been told about it yet, so its row
 * is stale by definition, and taking it would revert the change under whoever
 * just made it — visibly, seconds later, on the tablet in their hand.
 */
import * as Vue from 'vue';
import { tenant } from './tenant.js';

// Namespaced per salon — see tenant.js. Without it, a station that signs in as
// a second salon boots showing the first one's name, logo and colours, because
// the mirror is read before anything has asked the server who this is.
var MIRROR_BASE = 'dreamspa.dash.settings.v1';
var TABLE = 'tt_settings';

/* Every synced setting and what a fresh install gets.
 *
 * This list is also the filter: a key that is not in it is not a setting, so
 * hydrate drops it. A newer station writing a key this build has never heard
 * of therefore cannot put an unknown value where the board would read it —
 * the row stays in the table for the build that understands it.
 *
 * What is deliberately NOT here: which panel is open, which columns are
 * locked, whether finished rows are folded away, which module is showing, and
 * whether this station has closed the day. Those are facts about a screen, not
 * about the salon — a small tablet at the desk and the big monitor on the wall
 * want different answers, and syncing them would mean one station locking
 * every other station's column order. They stay in localStorage on their own.
 */
var DEFAULTS = {
  requestsOn: false,          // track client requests at all — see the store
  showNextUp: true,           // the "Next" column on each Next availability row
  // Whether the lobby's self check-in screen puts a wait beside a service the
  // salon cannot take right now. Salon-level rather than per-device on purpose:
  // it is a promise made to a client, so it cannot be one answer on the tablet
  // by the door and another on the one by the window. See js/components/check-in.js.
  checkinWaits: true,
  /* Where the lobby screen sends somebody whose wait is too long, and how long
   * "too long" is — see js/rules/booking.js.
   *
   * The link is a template with a `{service}` placeholder in it, filled from the
   * `booking_service_id` on each service, so somebody who tapped Pedicure lands
   * on Pedicure. Empty is the whole of the off switch: with no link the cutoff
   * does nothing, because a screen that says "book instead" and offers no way to
   * is worse than the waiting list it replaced.
   *
   * An hour is the default cutoff, and null is a salon that wants everybody
   * offered the queue however long it is. Salon-level rather than per-device for
   * the same reason as `checkinWaits` above: it is what a client is told, so it
   * cannot be one answer on the tablet by the door and another by the window. */
  checkinBookingUrl: '',
  checkinBookAfter: 60,
  // How the lobby screen looks and what it says — { colors, text }, null until
  // a salon changes something, and every key inside it optional. It is one key
  // rather than eighteen for the same reason `theme` and `hours` are: they are
  // set together, on one screen, and a per-field row would make a colour change
  // eighteen writes. See js/rules/checkin-look.js for what is in it and what an
  // absent value falls back to.
  checkinLook: null,
  // Which way round the grid is drawn — 'columns' (a column per technician,
  // a row per turn) or 'rows' (transposed: a row per technician, a column per
  // turn). Salon-level rather than per-device: it is how this salon reads its
  // board, and a desk tablet showing the day one way round while the wall
  // screen shows it the other is two different boards to learn. See the axis
  // section of js/components/turn-board.js.
  boardLayout: 'columns',
  // How a running square says it is running — 'light' (the blinking light this
  // console has always had) or 'bar' (a progress bar filling the timer strip
  // left to right as the booked length runs down). Salon-level for the same
  // reason as the layout above: it is how this salon reads its board, and the
  // wall screen and the desk tablet disagreeing about what a live square looks
  // like is two boards to learn. See the progress section of
  // js/components/turn-board.js.
  turnProgress: 'light',
  dueSoonMinutes: 10,         // how early a booking joins the bar's "Due now"
  conflictBufferMinutes: 5,   // how far behind a tech can be before it's a conflict
  // How far a booked square's start can be from the booking before the bar
  // marks it early or late. Set in the admin app; see js/rules/start-offset.js.
  startOffsetMinutes: 5,
  // What a sitting must cost to be a full turn, in dollars. Set in the admin
  // app; see js/rules/price-weight.js.
  fullTurnPrice: 40,
  // Whether a client who booked a technician goes to that technician ('book')
  // or to whoever is next in turn order ('turn'). Set in the admin app only;
  // see js/rules/booked-clients.js for how an unknown value reads.
  bookedClientsGo: 'book',
  autoStop: true,             // stop a turn when the booking system checks out
  autoStart: true,            // start the rest of a visit when a client sits
  theme: null,                // { preset, custom } — null means the default
  logo: null,                 // { dataUrl, width, height } for the top bar
  brand: '',                  // what the top bar calls this console
  plugins: null,              // { scheduling: { enabled, pluginId } }
  // When the doors open and shut, per weekday — { days: [ Sun … Sat ] }, each
  // entry { open, close } as "HH:MM" or null for a day the salon is closed.
  // Null all through means nobody has said, and the board asks nothing of the
  // clock: see the business-hours section of the store.
  hours: null
};

var state = Vue.reactive({});
var loaded = Vue.ref(false);   // true once the server has answered once

Object.keys(DEFAULTS).forEach(function (k) { state[k] = DEFAULTS[k]; });

/* ---------- the device mirror ---------- */

// True when this device already had a mirror, which is what says the adoption
// pass below has already run.
function readMirror() {
  try {
    var raw = window.localStorage.getItem(tenant.ns(MIRROR_BASE));
    var saved = raw ? JSON.parse(raw) : null;
    if (!saved || typeof saved !== 'object') return false;
    Object.keys(DEFAULTS).forEach(function (k) {
      if (k in saved) state[k] = saved[k];
    });
    return true;
  } catch (e) { return false; }   // corrupt or unavailable — the defaults are fine
}

function writeMirror() {
  try {
    var out = {};
    Object.keys(DEFAULTS).forEach(function (k) { out[k] = state[k]; });
    window.localStorage.setItem(tenant.ns(MIRROR_BASE), JSON.stringify(out));
    return null;
  } catch (e) {
    // Quota, realistically, and only the logo is big enough to hit it.
    // Silently dropping it would look like the change stuck until the next
    // reload put the old value back.
    return 'There was no room to save that on this device.';
  }
}

// Anything pushed before sync.js had loaded. Drained by `attach`.
var deferred = [];

/* ---------- adopting what a station already had ----------
 *
 * Before this table existed every one of these lived in its own localStorage
 * key, per device. A station that has been running for months has a logo, a
 * business name and a colour scheme in there, and coming up with none of them
 * because the settings moved would read as the console having lost the
 * salon's branding.
 *
 * So the first boot after the move seeds from the old keys and pushes what it
 * finds up, which is also how the salon's copy gets populated in the first
 * place — nobody has to go and set everything again.
 *
 * Only values that were actually SET are pushed. A station that never had a
 * logo has no logo key, seeds nothing and pushes nothing, so it cannot
 * overwrite the logo of a station that did. Seeded values sit in the write
 * queue until they go up, and the queue is exactly what stops the first
 * hydrate — against a table that is still empty — from resetting them.
 */
var LEGACY = [
  { key: 'theme', from: 'dreamspa.dash.theme', parse: 'json' },
  { key: 'logo', from: 'dreamspa.dash.logo', parse: 'json' },
  { key: 'plugins', from: 'dreamspa.dash.plugins', parse: 'json' },
  { key: 'brand', from: 'dreamspa.dash.brand', parse: 'text' },
  { key: 'showNextUp', from: 'dreamspa.dash.showNextUp', parse: 'onoff' },
  { key: 'autoStop', from: 'dreamspa.dash.autoStop', parse: 'onoff' },
  { key: 'autoStart', from: 'dreamspa.dash.autoStart', parse: 'onoff' },
  { key: 'requestsOn', from: 'dreamspa.dash.requests', parse: 'onoff' }
];

function adoptLegacy() {
  var found = [];
  LEGACY.forEach(function (m) {
    var raw = null;
    try { raw = window.localStorage.getItem(m.from); } catch (e) { return; }
    if (raw == null || raw === '') return;
    var value;
    if (m.parse === 'json') {
      try { value = JSON.parse(raw); } catch (e) { return; }
      if (value == null) return;
    } else if (m.parse === 'onoff') {
      if (raw !== 'on' && raw !== 'off') return;
      value = raw === 'on';
    } else {
      value = raw;
    }
    state[m.key] = value;
    found.push(m.key);
  });
  if (!found.length) return;
  writeMirror();
  // Queued rather than written: this runs at load, before there is a session
  // or possibly any internet. The queue is durable and replays either way.
  found.forEach(function (k) { push(k, state[k]); });
}

/* The write queue, handed over by sync.js rather than imported.
 *
 * These two files need each other: prefs pushes settings into the durable
 * queue, and sync asks prefs to drain whatever the adoption pass queued before
 * there was a queue to put it in. As globals that resolved itself, because
 * `push` looked window.DreamDash.sync up at call time and by then everything
 * had loaded. As modules it would be an import cycle, and the direction that
 * breaks is this one: sync already calls `attach`, so it can pass itself in.
 *
 * Which leaves prefs.js importing nothing but Vue and tenant.js — and tenant.js
 * is localStorage and string concatenation — so the settings rules can still be
 * tested without standing a Supabase client up first.
 */
var queue = null;

// Called once by sync.js as it finishes loading, to take anything the
// adoption pass queued before there was a queue to put it in.
function attach(sync) {
  queue = sync;
  deferred.splice(0).forEach(function (row) {
    sync.enqueue({ table: TABLE, type: 'upsert', onConflict: 'tenant_id,key', row: row });
  });
}

if (!readMirror()) adoptLegacy();

/* ---------- reading and writing ---------- */

function get(key) {
  return key in state ? state[key] : DEFAULTS[key];
}

// Into the durable queue, which applies it optimistically here and replays it
// until it lands. `queue` is null until sync.js calls attach — prefs.js is
// evaluated first so that the theme is available before the first paint, and
// the adoption pass runs at load. Anything pushed before then waits in
// `deferred` and goes up the moment there is a queue to put it in.
function push(key, value) {
  var row = { key: key, value: value === undefined ? null : value,
              updated_at: new Date().toISOString() };
  if (queue) { queue.enqueue({ table: TABLE, type: 'upsert', onConflict: 'tenant_id,key', row: row }); }
  else { deferred.push(row); }
}

// Applies the value everywhere at once: on screen, in the device mirror, and
// into the write queue. Returns a message if the device could not keep it,
// which only the logo is ever big enough to cause.
function set(key, value) {
  if (!(key in DEFAULTS)) return null;
  state[key] = value;
  var warn = writeMirror();
  push(key, value);
  return warn;
}

/* Take the server's copy, except for anything this station has not managed to
 * send yet. See the note at the top: those rows are stale by definition. */
function hydrate(rows) {
  var pending = queue && queue.pendingKeys ? queue.pendingKeys(TABLE) : {};
  var seen = {};
  (rows || []).forEach(function (r) {
    if (!(r.key in DEFAULTS) || pending[r.key]) return;
    seen[r.key] = true;
    state[r.key] = r.value === undefined ? null : r.value;
  });
  // A key with no row at all is the salon's answer too: it means nobody has
  // ever set it, so it goes back to the default rather than keeping whatever
  // this device happened to have. Otherwise a setting could never be reset
  // from another station, and a device that once held a value would keep it
  // for ever.
  Object.keys(DEFAULTS).forEach(function (k) {
    if (!seen[k] && !pending[k]) state[k] = DEFAULTS[k];
  });
  loaded.value = true;
  writeMirror();
}

/* The mirror was read at boot from whatever namespace this browser had cached,
 * and the session has since said the tenant is a different one. Carry the
 * stored copy across so the branding does not vanish and reappear.
 *
 * `previous` is null on a station's first sign-in after the cutover, which is
 * the un-namespaced key every install is using today — so this is the one-time
 * migration off it, in the same shape as the LEGACY adoption above and for the
 * same reason: a station that has been running for months should not come up
 * looking like a fresh install.
 */
function renamespace(previous) {
  tenant.move(MIRROR_BASE, previous);
  writeMirror();
}

export const prefs = {
  TABLE: TABLE,
  renamespace: renamespace,
  DEFAULTS: DEFAULTS,
  state: state,
  loaded: loaded,
  get: get,
  set: set,
  hydrate: hydrate,
  attach: attach
};
